<template>
  <div class="dropdown" :class="{'open' : open}">
    <div class="input-container" :class="{'input-active' : searchText}">
      <div v-if="searchText" class="close-icon icon" @click="clear">
        <img :src="closeIcon">
      </div>
      <input type="search"
             ref="search"
             v-model="searchText"
             @input="searchChanged"
             class="uk-search-input custom-text-truncate"
             @keydown.down="down"
             @keydown.up="up"
             @keydown.enter="updateList(matches[highlightIndex][1].title)"
             @keydown.esc="setOpen(false)"
             @blur="setOpen(false)"
             :placeholder="resolvePlaceholder"
             :class="{'uk-form-danger': error && submitted, 'input-fields': !stylePlaceholder, 'input-selected': stylePlaceholder}"
             v-on:keydown.enter.prevent
             @click="setOpen(!open)"
             @focusout="handleFocusOut">
      <div class="custom-overlay">
        <a class="toggle" @mousedown.prevent @click="setOpen(!open)">
          <div class="uk-search-icon icon"><img :src="dropdownOpen"></div>
          <div class="dropdown-closed-icon icon"><img :src="dropdownClosed"></div>
        </a>
      </div>
    </div>
    <input-validation-message :submitted="submitted" :error="error"
                              :message="message">
    </input-validation-message>
    <ul class="suggestion-list">
      <li v-for="(suggestion, index) in matches"
          @mousedown.prevent
          @click="updateList(suggestion[1].title)"
          @mouseover="hoverUpdate(index)"
          :class="{'activeRow': index === highlightIndex}">
        <div class="suggestion-label-container add-pointer">
          <div class="suggestion-label uk-text-truncate ellipsis-color">
            <a class="add-pointer suggestion">
              <img v-if="multiSelection" class="checkbox" :class="{'empty-checkbox': !isChecked(suggestion[1].title)}"
                   :src="activeCheckbox">
              <img v-if="multiSelection" class="checkbox" :class="{'empty-checkbox': isChecked(suggestion[1].title)}"
                   :src="emptyCheckBox">
              <input class="input-checkbox" :value="suggestion[1].title"
                     type="checkbox">
              {{suggestion[1].title}}
            </a>
          </div>
        </div>
      </li>
      <li v-if="!matches.length>0" class="no-suggestion-label"><a
        class="suggestion">{{$t('general.thereAreNoMatches')}}</a></li>
    </ul>
    <ul v-if="multiSelection" class="selected-list">
      <li class="add-pointer custom-text-truncate"
          v-for="selected in selectedOptions">
        <label class="option-label" @click="updateList(selected)">
          <img class="checkbox"
               :src="activeCheckbox">
          {{ selected }}
        </label>
      </li>
    </ul>
  </div>
</template>

<script>
  import iconUtil from '../../../utils/icon-util'
  import * as misc from '../../../enums/misc'
  import inputValidationMessage from '../../ui-components/validation/input-validation-message'

  export default {
    name: 'autocomplete-dropdown',
    props: ['options', 'placeholder', 'value', 'error', 'message', 'submitted', 'limit', 'selected', 'multiSelection', 'id', 'clearSelection', 'selectedStandard'],
    components: {
      inputValidationMessage
    },
    data() {
      return {
        searchText: '',
        selectedOptions: [],
        open: false,
        highlightIndex: 0,
        lastSearchText: '',
        dropdownOpen: iconUtil.resolve(misc.DROPDOWN_OPEN),
        closeIcon: iconUtil.resolve(misc.CLOSE_SLATE_14PX),
        dropdownClosed: iconUtil.resolve(misc.DROPDOWN_CLOSED),
        emptyCheckBox: iconUtil.resolve(misc.LRG_UNCHECKED),
        activeCheckbox: iconUtil.resolve(misc.LRG_CHECKED)
      }
    },
    computed: {
      matches() {
        if (this.options) {
          let matches = Object.entries(this.options).filter((option) => {
            let optionText = option[1].title.toUpperCase()
            return optionText.match(this.searchText.toUpperCase().replace(/\s+/g, '.+'))
          })
          return matches.length > 0 ? matches : {}
        }
      },
      resolvePlaceholder() {
        if (this.id === 'standard') {
          if (this.selectedOptions.length) {
            return this.truncatePlaceholder(this.selectedOptions[0])
          } else {
            return this.placeholder
          }
        } else {
          if (this.selectedOptions.length && !this.multiSelection) {
            return this.selectedOptions
          } else {
            return this.placeholder
          }
        }
      },
      stylePlaceholder() {
        return (!this.multiSelection && this.selectedOptions.length)
      }
    },
    watch: {
      value: (newValue) => {
        this.updateComponentWithValue(newValue)
      },
      selected(newValue) {
        for (let item of newValue) {
          this.updateList(item)
        }
      },
      clearSelection(value) {
        if (value) {
          this.selectedOptions = []
          this.searchText = ''
        }
      }
    },
    methods: {
      handleFocusOut() {
        if (!this.multiSelection) {
          this.clear()
        }
      },
      isChecked(selectedOption) {
        return this.selectedOptions.includes(selectedOption)
      },
      setOpen(isOpen) {
        this.open = isOpen
        if (this.open) {
          this.$refs.search.focus()
          this.lastSearchText = this.searchText
          this.searchText = ''
        } else if (this.searchText.trim() === '') {
          this.lastSearchText = this.searchText
          this.searchText = ''
        }
      },
      searchChanged() {
        if (!this.open) {
          this.setOpen(true)
        }
        this.highlightIndex = 0
      },
      updateComponentWithValue(newValue) {
        if (this.options) {
          if (Object.values(this.options).indexOf(newValue) > -1) {
            // Find the matching text for the supplied option value
            for (let text in this.options) {
              if (this.options.hasOwnProperty(text)) {
                if (this.options[text] === newValue) {
                  this.searchText = text
                }
              }
            }
          }
        }
      },
      up() {
        if (this.open) {
          if (this.highlightIndex > 0) {
            this.highlightIndex--
          }
        } else {
          this.setOpen(true)
        }
      },
      down() {
        if (this.open) {
          this.highlightIndex++
        } else {
          this.setOpen(true)
        }
      },
      removeSelectedOption(selectedOption) {
        let arr = this.selectedOptions
        this.selectedOptions = arr.filter((item) => {
          return item !== selectedOption
        })
      },
      removeStrandOption(selectedOption) {
        let arr = this.selectedOptions
        this.selectedOptions = arr.filter((item) => {
          return item.title !== selectedOption.title
        })
      },
      updateList(selectedOption) {
        if (this.multiSelection) {
          if (this.selectedOptions.length < this.limit) {
            if (!this.selectedOptions.includes(selectedOption)) {
              this.selectedOptions.push(selectedOption)
            } else {
              this.removeSelectedOption(selectedOption)
            }
          } else {
            if (this.selectedOptions.includes(selectedOption)) {
              this.removeSelectedOption(selectedOption)
            }
          }
        } else {
          this.selectedOptions = []
          this.selectedOptions.push(selectedOption)
          this.searchText = ''
          this.setOpen(false)
        }

        this.$emit('multiCheckList', this.selectedOptions)
      },
      setList(selectedOption) {
        this.selectedOptions = []
        this.selectedOptions.push(selectedOption)
        this.searchText = ''
        this.setOpen(false)
      },
      hoverUpdate(index) {
        this.highlightIndex = index
      },
      clear() {
        this.lastSearchText = this.searchText
        this.searchText = ''
      },
      truncatePlaceholder(placeholder) {
        if (placeholder.length > 50) {
          return placeholder.substring(0, 48) + '...'
        }
        return placeholder
      }
    },
    mounted() {
      this.updateComponentWithValue(this.value)
    },
    created() {
      if (this.selected) {
        for (let item of this.selected) {
          this.updateList(item)
        }
      } if (this.selectedStandard) {
        for (let item of this.selectedStandard) {
          this.setList(item)
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  @import '../../../styles/colours';
  @import '../../../styles/breakpoints';

  .dropdown {
    width: 100%;
    display: inline-block;
    position: relative;
  }

  .input-container {
    width: 100%;
    position: relative;
  }

  .close-icon {
    position: absolute;
    padding: 13px 0 0 13px;
  }

  .icon {
    width: 43px;
    height: 43px;
  }

  .custom-overlay {
    top: 30%;
    right: 8.5%;
    position: absolute;
  }

  .suggestion-list {
    background-color: $suggestion-list;
    border: $suggestion-list-border;
    opacity: 1;
    list-style: none;
    display: block;
    padding: 0;
    position: absolute;
    top: 45px;
    left: 0;
    z-index: 2;
    margin-top: 20px;
    margin-left: 0px !important;
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 300px;
    box-shadow: 3px 5px 8px 0 rgba(0, 0, 0, 0.24);
    width: 100%;

    li {
      width: 100%;
    }
  }

  .toggle {
    position: absolute;
  }

  .dropdown.open .suggestion-list {
    display: block;
  }

  .dropdown .suggestion-list {
    display: none;
  }

  .toggle .uk-search-icon {
    display: none;
  }

  .open .toggle .uk-search-icon {
    display: inline-block;
  }

  .open .toggle .dropdown-closed-icon {
    display: none;
  }

  .suggestion-list li {
    cursor: pointer;
    padding-left: 9px;
  }

  .suggestion-list li:hover, .activeRow {
    color: #fff;
    background-color: $suggestion-list-hover;

    a.suggestion {
      color: $light-blue;
    }
  }

  .input-fields {
    width: 100%;
    border-radius: 1px;
    box-shadow: $input-shadow;
    background-color: white;
    font-size: 18px;
    color: $share-input-text;
    height: 46px;
    padding-left: 13px;

    &:hover, &:focus {
      border: 2px solid $light-blue;
      box-shadow: 3px 5px 8px 0 rgba(0, 0, 0, 0.24);
    }
  }

  .input-active {
    .input-fields {
      padding-left: 36px;
    }
  }

  .input-active {
    .input-selected {
      padding-left: 36px;
    }
  }

  .input-fields::placeholder {
    font-style: italic;
    font-size: 16px;
    color: $slate;
  }

  .input-fields:-ms-input-placeholder {
    font-style: italic !important;
    font-size: 16px !important;
    color: $slate !important;
  }

  .input-fields::-ms-input-placeholder {
    font-style: italic;
    font-size: 16px;
    color: $slate;
  }

  .input-selected {
    width: 100%;
    border-radius: 1px;
    box-shadow: $input-shadow;
    background-color: white;
    font-size: 18px;
    color: $share-input-text;
    height: 46px;
    padding-left: 13px;

    &:hover, &:focus {
      border: 2px solid $light-blue;
      box-shadow: 3px 5px 8px 0 rgba(0, 0, 0, 0.24);
    }
  }

  .input-selected::placeholder {
    font-size: 16px;
    color: $content-text
  }

  .input-selected:-ms-input-placeholder {
    font-size: 16px !important;
    color: $content-text !important;
  }

  .input-selected::-ms-input-placeholder {
    font-size: 16px;
    color: $content-text
  }

  .active {
    opacity: 0.8;
    background-color: $app-background;
  }

  .option-label {
    font-size: 15px;
    margin: 0px 0px 0px 3px;
  }

  li {
    width: 100%;
    height: 37px;
    list-style-type: none;
  }

  .no-suggestion-label {
    padding: 12px 0px 11px 26px !important;
    font-size: 15px;
    color: #7a8b99;
    height: 15px !important;
  }

  ul {
    padding: 12px 0px 0px 0px;
    margin: 0 !important;
  }

  .suggestion-label {
    font-size: 15px;
    padding: 10px 0px 0px 0px !important;
  }

  .suggestions-label-container {
    height: 37px;
  }

  .input-checkbox {
    display: none;
  }

  .empty-checkbox {
    display: none;
  }

  .checkbox {
    height: 22px;
    width: 22px;
  }

  a.suggestion {
    text-decoration: none;
    color: $content-text;
  }

  a:hover {
    font-weight: normal;
    color: $content-text;
    text-decoration: none;
  }

  .uk-form-danger {
    color: $input-warning;
    border: 1px solid $input-warning !important;
  }

  .ellipsis-color {
    &:hover {
      color: $light-blue;
    }

    color: black;
  }

  .uk-text-truncate {
    max-width: 95%;
  }

  .custom-text-truncate {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  li.top-suggestions {
    font-size: 18px;
    font-weight: bold;
    background: white;
    margin-top: 10px;

    &:hover {
      color: $content-text;
      background: white;
    }
  }

  @media screen and (max-width: $breakpoint-large) {
    .custom-overlay {
      right: 10.5%;
    }
  }


</style>
